/**
 * Bookings config flags — TanStack Query hooks (Prompt 11F.8).
 *
 * Endpoint: GET/PATCH /v1/config/bookings.
 *
 * Deliberately narrow: only `trials_require_confirmation` and
 * `allow_force_override` are writable — those are the only two flags the
 * bookings service actually reads at write time (`_load_tenant_flags` in
 * `app.features.bookings.services.bookings`).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, tenantHeaderOverride } from '@core/api/client';
import { useTenant } from '@core/tenant/TenantContext';
import { useTenantSafeQuery } from '@core/tenant/useTenantSafeQuery';

export interface BookingsConfig {
  late_cancellation_window_hours: number;
  flags: {
    trials_require_confirmation: boolean;
    allow_force_override: boolean;
    [k: string]: boolean;
  };
}

export interface BookingsConfigUpdate {
  trials_require_confirmation?: boolean;
  allow_force_override?: boolean;
}

// Tenant id folded into the key — see the comment on `settingsConfigKeys`
// in @features/settings/api/config for why (queryClient.clear() alone does
// not reliably refetch an already-mounted useQuery on a tenant switch).
export const bookingsConfigKeys = {
  all: (tid: string) => ['config', 'bookings', tid] as const,
};

export function useBookingsConfig() {
  const { selectedTenantId, isAllMode } = useTenant();
  return useTenantSafeQuery({
    queryKey: bookingsConfigKeys.all(selectedTenantId),
    queryFn: () =>
      api.get<BookingsConfig>(
        '/v1/config/bookings',
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateBookingsConfig() {
  const qc = useQueryClient();
  const { selectedTenantId, isAllMode } = useTenant();
  return useMutation({
    mutationFn: (body: BookingsConfigUpdate) =>
      api.patch<BookingsConfig>(
        '/v1/config/bookings',
        body,
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    onSuccess: (data) => qc.setQueryData(bookingsConfigKeys.all(selectedTenantId), data),
  });
}
